// src/components/common/FileUploader.tsx
import React, { useState, useCallback, useRef } from 'react';
import './FileUploader.css'; // Import the new CSS

interface FileUploaderProps {
  /** Callback function triggered when a valid file is selected or dropped. */
  onFileSelected: (file: File) => void;
  /** String defining accepted file types (e.g., ".txt,.log,image/*"). */
  acceptedFileTypes?: string;
  /** Optional maximum file size in bytes. */
  maxFileSize?: number;
  /** Text label displayed in the drop zone. */
  label?: string;
  /** Text for the file selection button. */
  buttonText?: string;
  /** Optional additional CSS class for the container. */
  className?: string;
  /** Optional inline styles for the container. */
  style?: React.CSSProperties;
  /** Optional CSS class for the button. */
  buttonClassName?: string;
}

// Helper function to check if a file type is accepted
const isFileTypeAccepted = (file: File, accept?: string): boolean => {
    if (!accept || accept === '*') {
      return true; // No restriction or wildcard
    }
  
    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
    const fileTypeLower = file.type.toLowerCase();
    const fileNameLower = file.name.toLowerCase();
  
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        // Check file extension
        return fileNameLower.endsWith(type);
      }
      if (type.endsWith('/*')) {
        // Check MIME type wildcard (e.g., "image/*")
        return fileTypeLower.startsWith(type.slice(0, -1)); // Check the part before '/*'
      }
      // Check exact MIME type
      return fileTypeLower === type;
    });
};

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelected,
  acceptedFileTypes = '*', // Default to accepting all files
  maxFileSize,
  label = "Drag & Drop File Here",
  buttonText = "Select File",
  className = '',
  style = {},
  buttonClassName = 'deriq-action-button', // Default to your app's button style
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): boolean => {
    setError(null); // Clear previous errors

    // Basic type check (browser does some based on 'accept', but we can double-check MIME)
    // Note: 'accept' attribute is more of a hint; robust validation might need more checks.

    if (!isFileTypeAccepted(file, acceptedFileTypes)) {
        setError(`Invalid file type. Accepted types: ${acceptedFileTypes}`);
        return false;
      }

    if (maxFileSize && file.size > maxFileSize) {
      setError(`File is too large. Maximum size is ${Math.round(maxFileSize / 1024 / 1024)} MB.`);
      return false;
    }

    // Add more specific MIME type checks if needed based on acceptedFileTypes
    // Example: if (acceptedFileTypes === 'image/*' && !file.type.startsWith('image/')) ...

    return true;
  }, [acceptedFileTypes, maxFileSize]);

  const handleFileProcessing =  useCallback((file: File | null | undefined) => {
    if (file && validateFile(file)) {
      onFileSelected(file);
    }
  }, [validateFile, onFileSelected]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileProcessing(event.target.files?.[0]);
    // Reset file input to allow uploading the same file again
    if (event.target) {
        event.target.value = '';
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    setError(null);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileProcessing(event.dataTransfer.files[0]);
      event.dataTransfer.clearData();
    } else {
      // Optionally handle text drop if needed in the future,
      // but for a generic FileUploader, we usually ignore text drops.
      setError("Only file drops are supported.");
    }
  }, [handleFileProcessing]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setError(null); // Clear error on drag over
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Check if the leave is to a child element before setting isDragging to false
    const relatedTarget = event.relatedTarget as Node;
    if (!event.currentTarget.contains(relatedTarget)) {
        setIsDragging(false);
    }
  }, []);

  const handleButtonClick = () => {
    setError(null); // Clear error on button click
    fileInputRef.current?.click();
  };

  return (
    <div className={`file-uploader-container ${className}`} style={style}>
        <div
            className={`file-uploader-drop-area ${isDragging ? 'dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <p>{label}</p>
            <p>or</p>
            <button
                type="button" // Prevent form submission if nested
                onClick={handleButtonClick}
                // Combine default and passed button class names
                className={`file-uploader-button ${buttonClassName}`}
            >
                {buttonText}
            </button>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }} // Use style object
                accept={acceptedFileTypes}
                className="file-uploader-hidden-input" // Add class for potential targeting
            />
            {error && <p style={{ color: 'orange', marginTop: '0.5rem' }}>{error}</p>}
        </div>
    </div>
  );
};

export default FileUploader;
